import { useState, useEffect } from 'react'
import {
  Users, UserPlus, Shield, CheckCircle2, AlertTriangle,
  Trash2, Edit3, Key, Lock, Mail, Clock, RefreshCw, X, ShieldAlert, Check
} from 'lucide-react'
import { getUsers, createUser, updateUser, deleteUser, resetUserPassword } from '../api'
import { useAuth } from '../context/AuthContext'
import Modal from './Modal'

// Mirrors auth.validate_password_strength on the server. There is no default
// password any more: the previous build shipped the literal "P@ssword123!" as
// the default for every provisioned account.
const PASSWORD_MIN_LENGTH = 12

function passwordProblem(password) {
  if ((password || '').length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters long.`
  }
  const missing = []
  if (!/[a-z]/.test(password)) missing.push('one lowercase letter')
  if (!/[A-Z]/.test(password)) missing.push('one uppercase letter')
  if (!/\d/.test(password)) missing.push('one digit')
  if (!/[^A-Za-z0-9]/.test(password)) missing.push('one symbol')
  return missing.length ? `Password must contain at least ${missing.join(', ')}.` : null
}

export default function UserManagement() {
  // Identity comes from the validated session. This component previously took a
  // `currentUserRole` prop that defaulted to 'admin' and was never read.
  const { user: currentUser } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [successToast, setSuccessToast] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [resetTarget, setResetTarget] = useState(null)
  const [resetPassword, setResetPassword] = useState('')
  const [resetError, setResetError] = useState(null)
  const [resetting, setResetting] = useState(false)
  const [formError, setFormError] = useState(null)

  // Modal State
  const [showAddModal, setShowAddModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'employee',
    password: ''
  })

  // Edit Role State
  const [editingUserId, setEditingUserId] = useState(null)
  const [editRole, setEditRole] = useState('employee')

  const fetchUsersList = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getUsers()
      setUsers(res.users || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsersList()
  }, [])

  useEffect(() => {
    if (successToast) {
      const timer = setTimeout(() => setSuccessToast(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [successToast])

  const handleCreateUser = async (e) => {
    e.preventDefault()
    setFormError(null)

    if (!formData.name.trim() || !formData.email.trim()) {
      setFormError('Name and e-mail address are both required.')
      return
    }
    // The server requires a password and enforces a policy; validating here gives
    // the operator the message next to the field instead of after a round trip.
    const problem = passwordProblem(formData.password)
    if (problem) {
      setFormError(problem)
      return
    }

    setSubmitting(true)
    try {
      await createUser({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        role: formData.role,
        password: formData.password,
      })
      setSuccessToast(
        `Account created for ${formData.name}. They must change this password at first sign-in.`,
      )
      setShowAddModal(false)
      setFormData({ name: '', email: '', role: 'employee', password: '' })
      fetchUsersList()
    } catch (err) {
      setFormError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleSaveRole = async (userId) => {
    setError(null)
    try {
      const result = await updateUser(userId, { role: editRole })
      // A role change revokes that user's sessions server-side, which the operator
      // needs to know about because it signs the person out mid-task.
      const revoked = result?.sessions_revoked
      setSuccessToast(
        `Role updated to ${editRole === 'admin' ? 'Administrator' : 'DevOps Engineer'}`
        + (revoked ? `; signed out ${revoked} active session(s).` : '.'),
      )
      setEditingUserId(null)
      fetchUsersList()
    } catch (err) {
      setError(err.message)
    }
  }

  const handleToggleActive = async (target) => {
    setError(null)
    try {
      const result = await updateUser(target.id, { is_active: !target.is_active })
      setSuccessToast(
        `${target.name} ${target.is_active ? 'deactivated' : 'reactivated'}`
        + (result?.sessions_revoked ? `; signed out ${result.sessions_revoked} session(s).` : '.'),
      )
      fetchUsersList()
    } catch (err) {
      setError(err.message)
    }
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    const { id, name } = confirmDelete
    setConfirmDelete(null)
    setError(null)
    try {
      await deleteUser(id)
      setSuccessToast(`Account "${name}" deleted.`)
      fetchUsersList()
    } catch (err) {
      setError(err.message)
    }
  }

  const handleResetPassword = async (e) => {
    e.preventDefault()
    setResetError(null)
    const problem = passwordProblem(resetPassword)
    if (problem) {
      setResetError(problem)
      return
    }
    setResetting(true)
    try {
      const result = await resetUserPassword(resetTarget.id, resetPassword)
      setSuccessToast(
        `Password reset for ${resetTarget.name}`
        + (result?.sessions_revoked ? `; signed out ${result.sessions_revoked} session(s).` : '.')
        + ' They must change it at next sign-in.',
      )
      setResetTarget(null)
      setResetPassword('')
      fetchUsersList()
    } catch (err) {
      setResetError(err.message)
    } finally {
      setResetting(false)
    }
  }

  const getRoleBadge = (role) => {
    if (role?.toLowerCase() === 'admin') {
      return <span className="badge badge-alarm">Admin</span>
    }
    return <span className="badge badge-info">DevOps Engineer</span>
  }

  return (
    <div className="animate-in">
      <div className="flex-between mb-3">
        <div>
          <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '20px', fontWeight: 800, color: 'var(--ink-900)' }}>
            User Management & Role-Based Access Control (RBAC)
          </h2>
          <p style={{ fontSize: '13px', color: 'var(--ink-500)', marginTop: 2 }}>
            Provision team members, assign operational roles, and enforce security boundaries.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" onClick={fetchUsersList}>
            <RefreshCw size={15} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <UserPlus size={16} /> Add New User
          </button>
        </div>
      </div>

      {/* Notifications */}
      {successToast && (
        <div style={{
          background: '#ECFDF5',
          border: '1.5px solid var(--border-ink)',
          color: '#065F46',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: 'var(--shadow-solid-sm)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle2 size={18} color="#10B981" />
            <span style={{ fontSize: 13, fontWeight: 700 }}>{successToast}</span>
          </div>
          <button onClick={() => setSuccessToast(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065F46' }}>
            <X size={15} />
          </button>
        </div>
      )}

      {error && (
        <div style={{
          background: 'var(--brand-coral-bg)',
          border: '1.5px solid var(--border-ink)',
          color: '#9A2C14',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: 'var(--shadow-solid-sm)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={18} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>{error}</span>
          </div>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A2C14' }}>
            <X size={15} />
          </button>
        </div>
      )}

      {/* RBAC Role Cards (Admin & DevOps) */}
      <div className="stat-grid mb-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div className="stat-card">
          <div className="stat-card-icon red"><ShieldAlert size={20} /></div>
          <div className="stat-card-value">{users.filter(u => u.role === 'admin').length}</div>
          <div className="stat-card-label">Administrators</div>
          <div className="text-xs text-muted mt-1">Full control, user provisioning, credentials & security administration</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-icon blue"><Shield size={20} /></div>
          <div className="stat-card-value">{users.filter(u => u.role === 'employee' || u.role === 'devops').length}</div>
          <div className="stat-card-label">DevOps Engineers</div>
          <div className="text-xs text-muted mt-1">Telemetry ingestion, AI log analysis & executive report generation</div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card mb-3">
        <div className="card-header">
          <h3>Active Platform Users ({users.length})</h3>
        </div>
        <div style={{ padding: 0, overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>User Name</th>
                <th>Work Email</th>
                <th>Access Role</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 700, color: 'var(--ink-900)' }}>{u.name}</td>
                  <td className="font-mono text-xs text-muted">{u.email}</td>
                  <td>
                    {editingUserId === u.id ? (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <select
                          className="form-control"
                          value={editRole}
                          onChange={(e) => setEditRole(e.target.value)}
                          style={{ padding: '3px 8px', fontSize: 12, height: 30 }}
                        >
                          <option value="admin">Admin</option>
                          <option value="employee">DevOps</option>
                        </select>
                        <button
                          className="btn-icon"
                          onClick={() => handleSaveRole(u.id)}
                          title="Save Role"
                          style={{ color: '#047857' }}
                        >
                          <Check size={14} />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => setEditingUserId(null)}
                          title="Cancel"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      getRoleBadge(u.role)
                    )}
                  </td>
                  <td className="text-xs text-muted font-mono">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN') : 'Active'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        className="btn-icon"
                        aria-label={`Change the role of ${u.name}`}
                        title="Change role"
                        onClick={() => {
                          setEditingUserId(u.id)
                          setEditRole(u.role || 'employee')
                        }}
                      >
                        <Edit3 size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="btn-icon"
                        aria-label={`Reset the password for ${u.name}`}
                        title="Reset password"
                        onClick={() => { setResetTarget(u); setResetPassword(''); setResetError(null) }}
                      >
                        <Key size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="btn-icon"
                        aria-label={`${u.is_active ? 'Deactivate' : 'Reactivate'} ${u.name}`}
                        title={u.is_active ? 'Deactivate account' : 'Reactivate account'}
                        onClick={() => handleToggleActive(u)}
                        disabled={u.id === currentUser?.id}
                      >
                        <Lock size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="btn-icon"
                        aria-label={`Delete ${u.name}`}
                        title="Delete user"
                        onClick={() => setConfirmDelete({ id: u.id, name: u.name })}
                        disabled={u.id === currentUser?.id}
                        style={{ color: 'var(--brand-coral)' }}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Permissions Matrix */}
      <div className="card">
        <div className="card-header">
          <h3>2-Tier Operational Role Permissions Matrix</h3>
        </div>
        <div style={{ padding: 0, overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Capability / Feature</th>
                <th>Administrator</th>
                <th>DevOps Engineer</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ fontWeight: 600 }}>Fast-Track Report Studio & Telemetry Ingestion</td>
                <td style={{ color: '#047857', fontWeight: 700 }}>✓ Full Access</td>
                <td style={{ color: '#047857', fontWeight: 700 }}>✓ Full Access</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 600 }}>Client Infrastructure Onboarding & CloudFormation</td>
                <td style={{ color: '#047857', fontWeight: 700 }}>✓ Full Access</td>
                <td style={{ color: '#047857', fontWeight: 700 }}>✓ Full Access</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 600 }}>Security Audit Logs & VAPT Test Execution</td>
                <td style={{ color: '#047857', fontWeight: 700 }}>✓ Full Access</td>
                <td style={{ color: 'var(--brand-blue)', fontWeight: 700 }}>✓ View & Scan</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 600 }}>User Provisioning & Role Management</td>
                <td style={{ color: '#047857', fontWeight: 700 }}>✓ Full Access</td>
                <td style={{ color: 'var(--ink-400)' }}>✗ Restricted</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Add user */}
      <Modal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setFormError(null) }}
        title="Provision a new user account"
        maxWidth="520px"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { setShowAddModal(false); setFormError(null) }}
            >
              Cancel
            </button>
            <button type="submit" form="um-create-user" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create account'}
            </button>
          </>
        }
      >
        <form id="um-create-user" onSubmit={handleCreateUser}>
          {formError && (
            <div
              role="alert"
              style={{
                marginBottom: 14,
                padding: '10px 14px',
                background: 'var(--brand-coral-bg)',
                border: '1px solid var(--border-ink)',
                borderRadius: 6,
                color: '#9A2C14',
                fontSize: 12.5,
                fontWeight: 600,
              }}
            >
              {formError}
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="um-name">Full name</label>
            <input
              id="um-name"
              type="text"
              className="form-control"
              required
              autoComplete="off"
              placeholder="Name of the person who will use this account"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="um-email">Work e-mail address</label>
            <input
              id="um-email"
              type="email"
              className="form-control"
              required
              autoComplete="off"
              placeholder="name@yourcompany.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="um-role">Assigned role</label>
            <select
              id="um-role"
              className="form-control"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            >
              <option value="employee">DevOps Engineer — telemetry and reports</option>
              <option value="admin">Administrator — full access</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="um-password">Initial password</label>
            <input
              id="um-password"
              type="password"
              className="form-control"
              required
              autoComplete="new-password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              aria-describedby="um-password-hint"
            />
            <div id="um-password-hint" style={{ fontSize: 11.5, color: 'var(--ink-500)', marginTop: 4 }}>
              At least {PASSWORD_MIN_LENGTH} characters with an uppercase letter, a lowercase letter,
              a digit and a symbol. There is no default password: choose one, share it over a secure
              channel, and the user will be required to change it at first sign-in.
            </div>
          </div>
        </form>
      </Modal>

      {/* Reset password */}
      <Modal
        isOpen={resetTarget !== null}
        onClose={() => { setResetTarget(null); setResetError(null) }}
        title={resetTarget ? `Reset password — ${resetTarget.name}` : 'Reset password'}
        maxWidth="480px"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { setResetTarget(null); setResetError(null) }}
            >
              Cancel
            </button>
            <button type="submit" form="um-reset-password" className="btn btn-primary" disabled={resetting}>
              {resetting ? 'Resetting…' : 'Reset password'}
            </button>
          </>
        }
      >
        <form id="um-reset-password" onSubmit={handleResetPassword}>
          {resetError && (
            <div
              role="alert"
              style={{
                marginBottom: 14,
                padding: '10px 14px',
                background: 'var(--brand-coral-bg)',
                border: '1px solid var(--border-ink)',
                borderRadius: 6,
                color: '#9A2C14',
                fontSize: 12.5,
                fontWeight: 600,
              }}
            >
              {resetError}
            </div>
          )}
          <div className="form-group">
            <label className="form-label" htmlFor="um-new-password">New password</label>
            <input
              id="um-new-password"
              type="password"
              className="form-control"
              required
              autoComplete="new-password"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              aria-describedby="um-new-password-hint"
            />
            <div id="um-new-password-hint" style={{ fontSize: 11.5, color: 'var(--ink-500)', marginTop: 4 }}>
              Resetting signs the user out of every active session immediately and requires them to
              choose a new password at next sign-in.
            </div>
          </div>
        </form>
      </Modal>

      {/* Confirm delete */}
      <Modal
        isOpen={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title="Delete user account"
        maxWidth="460px"
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              style={{ background: 'var(--brand-coral)' }}
              onClick={handleConfirmDelete}
            >
              Delete account
            </button>
          </>
        }
      >
        <p style={{ color: 'var(--brand-coral)', fontSize: 13.5, fontWeight: 700, marginBottom: 8 }}>
          Delete <strong>{confirmDelete?.name}</strong>?
        </p>
        <p style={{ color: 'var(--ink-500)', fontSize: 12.5, margin: 0 }}>
          Their active sessions are revoked immediately. Audit records they generated are retained,
          because removing them would break the trail. This cannot be undone.
        </p>
      </Modal>
    </div>
  )
}
